import { useEffect, useCallback, useRef, useState } from "react";
import { useGameStore } from "../state/useGameStore";
import { useSettingsStore } from "../state/useSettingsStore";
import { DialogueBox } from "./DialogueBox";
import { ChoiceMenu } from "./ChoiceMenu";
import { PauseMenu } from "./PauseMenu";
import { SettingsPanel } from "./SettingsPanel";
import { SaveLoadScreen } from "./SaveLoadScreen";
import { Backlog } from "./Backlog";
import { VideoPlayer } from "./VideoPlayer";
import { PopupNotification } from "./PopupNotification";
import { DebugPanel } from "./DebugPanel";
import { QuickMenu } from "./QuickMenu";
import { SandboxPanel } from "./SandboxPanel";
import type { SaveLoad } from "../engine/SaveLoad";
import type { Rollback } from "../engine/Rollback";

interface GameRootProps {
  program: unknown;
  rollback: Rollback | null;
  saveLoad: SaveLoad | null;
}

const debugFromUrl = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("debug");

export function GameRoot({ rollback, saveLoad }: GameRootProps) {
  const [showDebug, setShowDebug] = useState(import.meta.env.DEV && debugFromUrl);
  const status = useGameStore((s) => s.status);
  const dialogue = useGameStore((s) => s.dialogue);
  const speaker = useGameStore((s) => s.speaker);
  const speakerColor = useGameStore((s) => s.speakerColor);
  const choices = useGameStore((s) => s.choices);
  const ended = useGameStore((s) => s.ended);
  const bus = useGameStore((s) => s.bus);
  const interpreter = useGameStore((s) => s.interpreter);
  const bb = useGameStore((s) => s.bb);
  const overlay = useGameStore((s) => s.overlay);
  const setOverlay = useGameStore((s) => s.setOverlay);
  const backlog = useGameStore((s) => s.backlog);
  const skipMode = useGameStore((s) => s.skipMode);
  const autoMode = useGameStore((s) => s.autoMode);
  const skipSeenMode = useGameStore((s) => s.skipSeenMode);
  const currentBeatId = useGameStore((s) => s.currentBeatId);
  const hudHidden = useGameStore((s) => s.hudHidden);
  const setSkipMode = useGameStore((s) => s.setSkipMode);
  const toggleAutoMode = useGameStore((s) => s.toggleAutoMode);
  const toggleSkipSeenMode = useGameStore((s) => s.toggleSkipSeenMode);
  const setSkipSeenMode = useGameStore((s) => s.setSkipSeenMode);
  const setHudHidden = useGameStore((s) => s.setHudHidden);
  const advance = useGameStore((s) => s.advance);
  const selectChoice = useGameStore((s) => s.selectChoice);
  const videoAsset = useGameStore((s) => s.videoAsset);
  const videoAutoplay = useGameStore((s) => s.videoAutoplay);
  const videoSkipAfter = useGameStore((s) => s.videoSkipAfter);
  const endVideo = useGameStore((s) => s.endVideo);
  const popupQueue = useGameStore((s) => s.popupQueue);
  const dismissPopup = useGameStore((s) => s.dismissPopup);
  const achievementUnlocked = useGameStore((s) => s.achievementUnlocked);
  const returnToTitle = useGameStore((s) => s.returnToTitle);
  const sandboxActive = useGameStore((s) => s.sandboxActive);
  const sandboxNarrationQueue = useGameStore((s) => s.sandboxNarrationQueue);
  const sandboxDismissNarration = useGameStore((s) => s.sandboxDismissNarration);
  const sandboxAssembleMode = useGameStore((s) => s.sandboxAssembleMode);
  const sandboxCancelAssemble = useGameStore((s) => s.sandboxCancelAssemble);
  const minigameId = useGameStore((s) => s.minigameId);

  const autoDelay = useSettingsStore((s) => s.autoDelay);
  const theme = useSettingsStore((s) => s.theme);

  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoTimer = useCallback(() => {
    if (autoTimerRef.current !== null) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  // Skip mode: auto-advance rapidly through narrate/say
  useEffect(() => {
    if (skipMode && status === "waiting" && overlay === "none") {
      const t = setTimeout(() => advance(), 50);
      return () => clearTimeout(t);
    }
  }, [skipMode, status, currentBeatId, overlay, advance]);

  // Auto mode: advance after configurable delay
  useEffect(() => {
    clearAutoTimer();
    if (autoMode && !skipMode && status === "waiting" && overlay === "none") {
      autoTimerRef.current = setTimeout(() => {
        autoTimerRef.current = null;
        advance();
      }, autoDelay * 1000);
    }
    return clearAutoTimer;
  }, [autoMode, skipMode, status, currentBeatId, overlay, autoDelay, advance, clearAutoTimer]);

  // Skip-seen mode: advance instantly past already-read beats, stop at unseen text or choices
  useEffect(() => {
    if (!skipSeenMode || overlay !== "none") return;
    if (status === "ended") {
      setSkipSeenMode(false);
      return;
    }
    if (status === "waiting" && currentBeatId) {
      if (interpreter.isSeenBeat(currentBeatId)) {
        const t = setTimeout(() => advance(), 0);
        return () => clearTimeout(t);
      } else {
        setSkipSeenMode(false);
      }
    }
  }, [skipSeenMode, status, currentBeatId, overlay, advance, interpreter, setSkipSeenMode]);

  // Click handler: restore HUD when hidden
  const handleClick = useCallback(() => {
    if (hudHidden) setHudHidden(false);
  }, [hudHidden, setHudHidden]);

  // Keyboard handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (import.meta.env.DEV && e.key === "F12") {
        e.preventDefault();
        setShowDebug((v: boolean) => !v);
        return;
      }

      // Any key restores HUD when hidden (H is toggle, all others just restore)
      if (hudHidden) {
        e.preventDefault();
        setHudHidden(false);
        return;
      }

      if (overlay !== "none") return;

      // H: toggle HUD visibility (only when overlay is closed)
      if (e.key === "h" || e.key === "H") {
        e.preventDefault();
        setHudHidden(true);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        if (status === "minigame" && minigameId) {
          bus.emit("minigame.complete", { id: minigameId, result: "cancelled" });
        } else if (sandboxAssembleMode) {
          sandboxCancelAssemble();
        } else {
          setOverlay("pause");
        }
        return;
      }

      if (status === "minigame") return;

      if (e.key === "a" || e.key === "A") {
        e.preventDefault();
        toggleAutoMode();
        return;
      }

      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        setOverlay("save");
        return;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        toggleSkipSeenMode();
        return;
      }

      if (e.key === "Control") {
        setSkipMode(true);
        return;
      }

      if (e.key === "l" || e.key === "L") {
        e.preventDefault();
        setOverlay("backlog");
        return;
      }

      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (status === "sandbox" && sandboxNarrationQueue.length > 0) {
          sandboxDismissNarration();
        } else if (status === "waiting") {
          advance();
        }
      }
    },
    [overlay, status, advance, setOverlay, toggleAutoMode, toggleSkipSeenMode, setSkipMode, hudHidden, setHudHidden, sandboxNarrationQueue, sandboxDismissNarration, sandboxAssembleMode, sandboxCancelAssemble, minigameId, bus],
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Control") {
        setSkipMode(false);
      }
    },
    [setSkipMode],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // On load complete from save/load screen, close overlays and ensure we're playing
  const handleLoadComplete = useCallback(() => {
    setOverlay("none");
  }, [setOverlay]);

  const handleReturnToTitle = useCallback(() => {
    returnToTitle();
  }, [returnToTitle]);

  return (
    <div
      className={hudHidden ? `theme-${theme} hud-hidden` : `theme-${theme}`}
      onClick={handleClick}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
      }}
    >
      <div
        className="game-hud-overlay"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          aspectRatio: "16 / 9",
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          maxHeight: "100%",
          pointerEvents: "none",
        }}
      >
        {videoAsset && (
          <div style={{ pointerEvents: "auto", position: "absolute", inset: 0, zIndex: 200 }}>
            <VideoPlayer
              asset={videoAsset}
              autoplay={videoAutoplay}
              skipAfter={videoSkipAfter}
              onEnd={endVideo}
            />
          </div>
        )}

        {popupQueue.length > 0 && (
          <div style={{ pointerEvents: "auto" }}>
            <PopupNotification queue={popupQueue} onDismiss={dismissPopup} />
          </div>
        )}

        {/* Mode indicator */}
        {(skipMode || autoMode || skipSeenMode) && overlay === "none" && (
          <div
            style={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              padding: "0.3rem 0.75rem",
              background: "rgba(0, 0, 0, 0.6)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "4px",
              fontSize: "0.8rem",
              opacity: 0.7,
              pointerEvents: "none",
            }}
          >
            {skipMode ? "SKIP" : autoMode ? "AUTO" : "SKIP-SEEN"}
          </div>
        )}

        {/* Dialogue box */}
        {status === "waiting" && dialogue && overlay === "none" && (
          <div style={{ pointerEvents: "auto" }}>
            <DialogueBox
              text={dialogue}
              speaker={speaker}
              speakerColor={speakerColor}
              onAdvance={advance}
              skipMode={skipMode}
              skipSeen={skipSeenMode && currentBeatId ? interpreter.isSeenBeat(currentBeatId) : false}
              autoMode={autoMode}
            />
          </div>
        )}

        {/* Choice menu */}
        {status === "choosing" && choices && overlay === "none" && (
          <div style={{ pointerEvents: "auto" }}>
            <ChoiceMenu options={choices} onSelect={selectChoice} />
          </div>
        )}

        {/* Sandbox panel */}
        {sandboxActive && overlay === "none" && (
          <div style={{ pointerEvents: "auto" }}>
            <SandboxPanel />
          </div>
        )}

        {/* Story end */}
        {ended && overlay === "none" && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "1.5rem",
              opacity: 0.6,
            }}
          >
            The End.
          </div>
        )}

        {/* Pause menu overlay */}
        {overlay === "pause" && (
          <div style={{ pointerEvents: "auto" }}>
            <PauseMenu
              onResume={() => setOverlay("none")}
              onSave={() => setOverlay("save")}
              onLoad={() => setOverlay("load")}
              onSettings={() => setOverlay("settings")}
              onReturnToTitle={handleReturnToTitle}
              achievements={(interpreter.getProgram()?.globals?.achievements ?? {}) as Record<string, import("../engine/AchievementEngine").AchievementDef>}
              achievementUnlocked={achievementUnlocked}
            />
          </div>
        )}

        {/* Save screen */}
        {overlay === "save" && saveLoad && (
          <div style={{ pointerEvents: "auto" }}>
            <SaveLoadScreen
              mode="save"
              saveLoad={saveLoad}
              onClose={() => setOverlay("pause")}
            />
          </div>
        )}

        {/* Load screen */}
        {overlay === "load" && saveLoad && (
          <div style={{ pointerEvents: "auto" }}>
            <SaveLoadScreen
              mode="load"
              saveLoad={saveLoad}
              onClose={() => setOverlay("pause")}
              onLoadComplete={handleLoadComplete}
            />
          </div>
        )}

        {/* Settings panel */}
        {overlay === "settings" && (
          <div style={{ pointerEvents: "auto" }}>
            <SettingsPanel onClose={() => setOverlay("pause")} />
          </div>
        )}

        {/* Backlog */}
        {overlay === "backlog" && (
          <div style={{ pointerEvents: "auto" }}>
            <Backlog
              entries={backlog}
              onClose={() => setOverlay("none")}
            />
          </div>
        )}

        {/* Quick menu — hover-reveal strip at bottom */}
        {overlay === "none" && !minigameId && (
          <div style={{ pointerEvents: "auto" }}>
            <QuickMenu onRewind={() => rollback?.rewind()} saveLoad={saveLoad} />
          </div>
        )}

        {/* Debug panel (dev only) */}
        {showDebug && (
          <DebugPanel bb={bb} bus={bus} interpreter={interpreter} />
        )}
      </div>
    </div>
  );
}
